/**
 * WHAT A DOOR SAYS ABOUT ITSELF, in one set of prose builders.
 *
 * This file was the INTAKE — a state machine that walked a knocker through
 * "where should a reply reach you", then one question per extra channel the
 * door's House Rules asked for, then the links. Ruling R-0827-1 took the
 * at-door intake away when knocking moved into the console, and ruling R-0828-2
 * retired the channel ask itself: the console is the only channel people talk
 * to people in, so a door asks for no WhatsApp, no Telegram and no phone
 * number, and there is no second question to walk anybody through.
 *
 * What survives is the part that was never a state machine — the sentences a
 * door is described in. They live in ONE place because the public plate
 * (`door-card.tsx`) and the console's compose thread (`shell/compose-pane.tsx`)
 * both print them, and a second copy of this prose is how the two come to
 * describe the same door differently.
 */
import type { PublicDoor } from "../../lib/api";

/**
 * HOW HARD THIS DOOR IS, AND THE SERVER SAYS IT.
 *
 * There is nothing to compute here any more. `difficultyOf()` used to map a
 * `{p25, p75}` band through two client-side thresholds into a phrase, which
 * meant two independent prose mappings — one here, one on the server — with
 * different inputs and different thresholds, describing the same door two
 * different ways. Ruling C3 removed the band from the wire entirely and
 * economics-wire §6.1 replaced it with the sentence the server already decided,
 * so the door page and the knock portal cannot disagree.
 *
 * Empty is a door that said nothing; the header renders no note for it.
 */
export function difficultyLine(door: PublicDoor): string | null {
  return door.difficulty.trim() ? door.difficulty.trim() : null;
}

/** A door with nothing published says so rather than showing an empty list. */
export const EMPTY_DOOR_COPY = "This door is new. Knock and find out.";

export const GETS_THROUGH_LEAD = "What gets through:";

/**
 * Where the sender is standing, and who will be answering. The persona is part
 * of the same breath rather than a bubble of its own: it is a fact about the
 * door, and a door that has not named one says nothing.
 */
export function openingLine(door: PublicDoor): string {
  const opening = `You’re knocking at ${door.display_name}’s door.`;
  return door.persona_name ? `${opening} ${door.persona_name} answers this door.` : opening;
}

/**
 * The owner's own line (FR-3.8.1), spoken by the doorman as a fact about whose
 * door this is — ALONGSIDE the public line, never instead of it: one says who
 * this is, the other says what they will hear about. A door with no bio adds no
 * line at all, and nothing is invented to fill the gap.
 */
export function bioLine(door: PublicDoor): string | null {
  return door.bio;
}

/**
 * The door's public line, spoken in the thread (ruling R-0910-10). One line the
 * owner wrote for strangers — never a section of their House Rules, which is
 * private without exception now — and the plain true sentence for a door that
 * has not written one.
 */
export function getsThroughLine(door: PublicDoor): string {
  if (!door.public_line) return EMPTY_DOOR_COPY;
  return `${GETS_THROUGH_LEAD}\n${door.public_line}`;
}

export function hearingIntro(door: PublicDoor): string {
  return `I ask, you answer — in your own words. Clear the bar and ${door.display_name} reads it.`;
}

/** No figure, ever, in the thread. */
export const HEARING_OPENED_LINE = "Hearing opened.";

/**
 * What the thread says when the knock met a plan with no room left: nothing to
 * approve and nothing to argue with — the allowance is the only thing
 * outstanding. It said "once the credits are there" until ruling R-0916-1, and
 * it names no figure now for the same reason it named none then.
 */
export const AWAITING_ALLOWANCE_LINE = "The hearing opens once your allowance has room.";

/**
 * THE SAME MOMENT ON A TIER THAT BUYS NO WORK (ruling C10).
 *
 * An allowance is not what is outstanding for a Free knocker — a plan is, and
 * telling them to wait for room points at an allowance they were never given.
 * The paywall this row sits under offers a rung, so the row says the thing that
 * rung is for.
 */
export const AWAITING_PLAN_LINE = "The hearing opens once you are on a plan.";

/**
 * WHY AN ACCOUNT'S FIRST KNOCK TAKES A MOMENT (rulings R-0906-3, R-0911-1).
 *
 * The server checks a knocker once per account, on the first knock they ever
 * send, and since the ruling the hearing does not begin until that check has
 * landed. So the wait is EXPLAINED rather
 * than presented as a slow send: the room says what is happening, in the
 * room's own voice — this is not the doorman speaking, and it must never
 * render as one, because the doorman has not been asked anything yet.
 *
 * IT SAYS "HEARING", NOT "DOOR" (owner, 2026-09-07). A door OPENING is the
 * let-through at the end — the recipient's decision — and borrowing that
 * phrase for the start of the grilling would promise the knocker the one
 * thing nobody has decided yet.
 *
 * No cost, no score, no signal: the client is told a check is running and
 * nothing else, which is all `sender_check_due` carries.
 *
 * IT NAMES NO DURATION (owner, 2026-09-07). The measured probe is ~30s (see
 * `PROBE_DEADLINE_MS`), and a sentence promising "a few seconds" is a promise
 * the vendor does not keep — but the answer to that is the SPINNER beside the
 * line, not a number the knocker then watches us miss. A moving indicator says
 * "still working" for as long as it takes and cannot be wrong.
 */
export const CHECKING_SENDER_LINE = "Checking your account before the hearing starts.";
